import { Severity } from '@kirin/api-client/contracts/shared/severity';
import {
  formatSubPolicyDescription,
  formatSubPolicyLabel,
  formatSubPolicySections,
} from '@kirin/api-client/helpers/alerts';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import AlertCard from './AlertCard';
import { useUserPolicies } from '@/hooks/api/useUserPolicies';
import {
  PolicyKey,
  PolicyMode,
  type PolicyList,
} from '@kirin/api-client/contracts/policies';

/**
 * Blocked MCP server demo.
 *
 * KIR-3725 retired the standalone "Untrusted MCP Server" alert, which this demo
 * used to walk the customer into: it told them to arm MCP Servers Security and
 * expect an alert purely because `mcp-server-database` was not on Kirin's
 * trusted list. That alert no longer fires — "unknown" is not "bad" — so the
 * demo now exercises the control that replaced it: the customer's own Block
 * list, which produces a `blocked_by_policy` alert.
 */
const BLOCKED_MCP_CONFIG = `{
  "mcpServers": {
    "community-server": {
      "command": "npx",
      "args": ["mcp-server-database"]
    }
  }
}`;

const BLOCKED_SERVER_NAME = 'community-server';

const isMcpSourcesPolicy = (policy: { key: string }): boolean => {
  return policy.key === PolicyKey.McpServerSources;
};

const getMcpSourcesMode = (
  data: PolicyList['policies'] | undefined
): PolicyMode => {
  if (!data || data.length === 0) return PolicyMode.Off;
  const mcpPolicy = data.find(isMcpSourcesPolicy);
  return (mcpPolicy?.mode as PolicyMode | undefined) ?? PolicyMode.Off;
};

export default function BlockedMcpServerDemo() {
  const { isLoading, isError, data } = useUserPolicies({
    select: policies => policies.filter(isMcpSourcesPolicy),
  });

  const mcpSourcesMode = getMcpSourcesMode(
    data as PolicyList['policies'] | undefined
  );
  const showMCPInfo =
    !isLoading && !isError && mcpSourcesMode === PolicyMode.Off;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold mb-2">Steps to reproduce:</h3>
        <ol className="list-decimal ml-5 space-y-2 text-sm">
          <li>
            Set the <span className="font-semibold">MCP Servers</span> policy to{' '}
            <span className="font-semibold">Monitor</span> or{' '}
            <span className="font-semibold">Enforce</span> in your
            organization&apos;s policy settings
          </li>
          <li>
            Add{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              {BLOCKED_SERVER_NAME}
            </code>{' '}
            to the policy&apos;s{' '}
            <span className="font-semibold">Blocked MCP servers</span> list
          </li>
          <li>Copy the MCP server configuration below using the button</li>
          <li>
            Open or create:{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              .cursor/mcp.json
            </code>
          </li>
          <li>Paste the configuration into the file</li>
          <li>Save the file</li>
          <li>Check Kirin for the security alert</li>
        </ol>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">MCP server configuration:</h3>
          <CopyToClipboardButton
            text={BLOCKED_MCP_CONFIG}
            variant="outline"
            size="sm"
            className="gap-2"
          />
        </div>
        <HighlightBox
          variant="neutral"
          padding="lg"
          className="font-mono text-sm whitespace-pre-wrap overflow-x-auto"
        >
          {BLOCKED_MCP_CONFIG}
        </HighlightBox>
      </div>

      <div>
        <h3 className="font-semibold mb-2">
          Why the Block list is the right control here:
        </h3>
        <ul className="list-disc ml-5 space-y-1 text-sm text-muted-foreground">
          <li>
            Package{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              mcp-server-database
            </code>{' '}
            is not on Kirin&apos;s trusted list — but that alone is not a
            problem. Plenty of valid MCP servers are simply not listed yet.
          </li>
          <li>
            It is not from the official {'@modelcontextprotocol'} namespace, and
            its name resembles trusted packages (mcp-server-*) — a common
            pattern in package impersonation. That is a judgement call about
            your organization, so you make it explicitly.
          </li>
          <li>
            Adding it to the Block list is that decision. The Block list
            overrides the Trusted list and takes effect even when every
            detection policy comes back clean.
          </li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold mb-2">
          Expected alert in Kirin (MCP Servers policy in Monitor/Enforce):
        </h3>
        <AlertCard
          title={formatSubPolicyLabel(
            'mcpServersBlockedList',
            'blocked_by_policy'
          )}
          severity={Severity.High}
          description={formatSubPolicyDescription(
            'mcpServersBlockedList',
            'blocked_by_policy',
            "Configured MCP server is on the organization's blocked-MCP-server list."
          )}
          descriptionSections={formatSubPolicySections(
            'mcpServersBlockedList',
            'blocked_by_policy'
          )}
          details={`Source: ${BLOCKED_SERVER_NAME}`}
        />
      </div>
      {showMCPInfo && (
        <div>
          <h3 className="font-semibold mb-2">Important Note</h3>
          <HighlightBox variant={'info'}>
            To trigger a <span className="font-bold">Blocked MCP server</span>{' '}
            alert, set the <span className="font-bold">MCP Servers</span> policy
            to <span className="font-bold">Monitor</span> or{' '}
            <span className="font-bold">Enforce</span> and add{' '}
            <span className="font-bold">{BLOCKED_SERVER_NAME}</span> to its
            Blocked MCP servers list. The policy is currently{' '}
            <span className="font-bold">Off</span>, so nothing will be reported.
          </HighlightBox>
        </div>
      )}
    </div>
  );
}
